import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { CreateChatRequest } from 'ldt-chat-api';
import { HeapUtils } from 'src/app/api-client/heap.utils';
import { ChatCreditUsage, ChatCreditsService } from 'src/app/core/services/chat-credits.service';
import { SubscriptionDialogComponent } from 'src/app/settings/billing/subscription-dialog/subscription-dialog.component';
import {
  NEW_CHAT_PILL_ORDER,
  NEW_CHAT_RUNG_LABELS,
  NEW_CHAT_TOPICS,
  NEW_CHAT_VISIBLE_COUNT,
  NewChatTopic,
} from './new-chat-topics';

export type NewChatMode = 'snapshot' | 'deepresearch';

/** Payload emitted when the user submits a new chat prompt. */
export interface NewChatSubmission {
  prompt: string;
  mode: CreateChatRequest.ModeEnum;
}

interface ExampleRung {
  label: string;
  text: string;
}

@Component({
  selector: 'app-new-chat',
  templateUrl: './new-chat.component.html',
  styleUrls: ['./new-chat.component.scss'],
  standalone: false,
})
export class NewChatComponent implements OnInit, OnDestroy {
  @Output() messageSubmitted = new EventEmitter<NewChatSubmission>();

  @ViewChild('chatInput') chatInput?: ElementRef<HTMLTextAreaElement>;

  currentPrompt: string = '';
  isLoading = false;

  // Live chat usage for the active org (drives the three usage treatments below).
  usage: ChatCreditUsage | null = null;
  private usageSub?: Subscription;

  expanded = false;
  openCategory: string | null = null;
  mode: NewChatMode = 'snapshot';

  // Stable state for the examples panel (set on pill click, not recomputed per CD cycle).
  activeCategory: NewChatTopic | null = null;
  exampleRungs: ExampleRung[] = [];

  readonly modeTooltips: Record<NewChatMode, string> = {
    snapshot: 'Summary findings, key numbers, and headline visuals. 1–2 minutes.',
    deepresearch: 'Multi-step analysis, charts, and full context. 2–5 minutes.',
  };

  // Maps the UI mode toggle to the chat API `mode` param.
  private readonly apiModeByMode: Record<NewChatMode, CreateChatRequest.ModeEnum> = {
    snapshot: CreateChatRequest.ModeEnum.Snapshot,
    deepresearch: CreateChatRequest.ModeEnum.DeepResearch,
  };

  private readonly allPills: NewChatTopic[] = NEW_CHAT_PILL_ORDER.map((key) => ({
    key,
    ...NEW_CHAT_TOPICS[key],
  }));
  readonly hiddenCount = NEW_CHAT_PILL_ORDER.length - NEW_CHAT_VISIBLE_COUNT;
  // Stable array reference; only reassigned when `expanded` toggles.
  visiblePills: NewChatTopic[] = this.allPills.slice(0, NEW_CHAT_VISIBLE_COUNT);

  constructor(
    private chatCreditsService: ChatCreditsService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.usageSub = this.chatCreditsService
      .getChatCreditUsage()
      .subscribe((usage) => (this.usage = usage));
  }

  ngOnDestroy(): void {
    this.usageSub?.unsubscribe();
  }

  trackByPillKey(_index: number, pill: NewChatTopic): string {
    return pill.key;
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
    this.visiblePills = this.expanded
      ? this.allPills
      : this.allPills.slice(0, NEW_CHAT_VISIBLE_COUNT);
  }

  handlePillClick(key: string) {
    const topic = NEW_CHAT_TOPICS[key];
    HeapUtils.track('workforce_new_chat_pill_click', {
      topic_key: key,
      topic_label: topic.label,
    });

    if (this.openCategory === key) {
      this.closeCategory();
      return;
    }
    this.openCategory = key;
    this.activeCategory = { key, ...topic };
    this.exampleRungs = [
      { label: NEW_CHAT_RUNG_LABELS[0], text: topic.r1 },
      { label: NEW_CHAT_RUNG_LABELS[1], text: topic.r3 },
      { label: NEW_CHAT_RUNG_LABELS[2], text: topic.r4 },
    ];
  }

  closeCategory() {
    this.openCategory = null;
    this.activeCategory = null;
    this.exampleRungs = [];
  }

  setMode(mode: NewChatMode) {
    this.mode = mode;
  }

  // ----- Usage / quota helpers (shared by all three usage treatments) -----

  private limitForMode(mode: NewChatMode): number | 'unlimited' | null {
    if (!this.usage) {
      return null;
    }
    return mode === 'snapshot' ? this.usage.snapshotLimit : this.usage.deepResearchLimit;
  }

  private usedForMode(mode: NewChatMode): number {
    if (!this.usage) {
      return 0;
    }
    return mode === 'snapshot' ? this.usage.snapshotUsed : this.usage.deepResearchUsed;
  }

  isUnlimitedMode(mode: NewChatMode): boolean {
    const limit = this.limitForMode(mode);
    return limit === 'unlimited' || limit === null;
  }

  remainingForMode(mode: NewChatMode): number | null {
    const limit = this.limitForMode(mode);
    if (typeof limit !== 'number') {
      return null;
    }
    return Math.max(0, limit - this.usedForMode(mode));
  }

  percentForMode(mode: NewChatMode): number {
    const limit = this.limitForMode(mode);
    if (typeof limit !== 'number' || limit <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((this.usedForMode(mode) / limit) * 100));
  }

  modeLabel(mode: NewChatMode): string {
    return mode === 'snapshot' ? 'snapshot' : 'deep research';
  }

  // A: composer caption (only shown when the active mode is metered).
  showUsageCaption(): boolean {
    return !!this.usage && !this.isUnlimitedMode(this.mode);
  }

  // A': caption shown when the active mode is explicitly unlimited.
  showUnlimitedCaption(): boolean {
    return !!this.usage && this.limitForMode(this.mode) === 'unlimited';
  }

  activeRemaining(): number | null {
    return this.remainingForMode(this.mode);
  }

  activeLimitValue(): number | null {
    const limit = this.limitForMode(this.mode);
    return typeof limit === 'number' ? limit : null;
  }

  isAtLimit(): boolean {
    const remaining = this.remainingForMode(this.mode);
    return remaining !== null && remaining <= 0;
  }

  usageColorClass(): string {
    switch (
      ChatCreditsService.usageSeverity(
        this.percentForMode(this.mode),
        this.remainingForMode(this.mode)
      )
    ) {
      case 'danger':
        return 'nc-usage--danger';
      case 'warn':
        return 'nc-usage--warn';
      default:
        return '';
    }
  }

  goToBilling(): void {
    // Pop the chat plan picker in place rather than navigating away from the composer.
    try {
      this.dialog.open(SubscriptionDialogComponent, {
        backdropClass: 'darker-dialog-backdrop',
        panelClass: 'subscription-dialog',
        data: { subscriptionType: 'chat' },
      });
    } catch {
      // The "Ask the workforce" composer is Workforce-only, so billing lives at /billing.
      this.router.navigate(['/billing']);
    }
  }

  handleExampleClick(rung: ExampleRung, index: number) {
    HeapUtils.track('workforce_new_chat_rung_click', {
      topic_key: this.openCategory,
      topic_label: this.activeCategory?.label,
      rung_number: index + 1,
      rung_label: rung.label,
    });

    this.currentPrompt = rung.text;
    this.closeCategory();
    setTimeout(() => {
      const ta = this.chatInput?.nativeElement;
      if (ta) {
        ta.focus();
        ta.setSelectionRange(ta.value.length, ta.value.length);
        ta.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  }

  sendMessage() {
    if (!this.currentPrompt?.trim()) {
      return;
    }

    this.messageSubmitted.emit({
      prompt: this.currentPrompt.trim(),
      mode: this.apiModeByMode[this.mode],
    });
    this.currentPrompt = '';
  }
}
